import { Modal, notification, Spin, Table, Tag, Tooltip } from "antd";
import { Breakpoint } from "antd";
import {
  AreaChartOutlined,
  CheckCircleOutlined,
  EyeTwoTone,
  ExclamationCircleFilled,
  FileExcelTwoTone,
  MinusCircleOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import SweepBase from "../../../../../models/Sweep";
import SweepService from "../../../../../services/SweepService";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../../../context/LanguageContext";
import { getWarehouseReferenceColor } from "../../../../../common/warehouseColorReferences";


interface SweepsTableProps {
  sweeps: SweepBase[];
  page: number;
  pageSize: number;
  total:number;
  onPageChange: (page: number, pageSize: number) => void;
  onSort?: (column_key: string, order: string) => void;
  onUpdateContent: () => void;
  writePermission: Boolean;
  securityPermission: Boolean;
  isMobile: Boolean;
  loading: boolean;
}

const SweepsTable = (props: SweepsTableProps) => {
  const { language } = useLanguage();
  const creatingTimeoutRef = useRef<{ [key: string]: boolean }>({});
  const { confirm } = Modal;
  const showConfirm = (type: string, handler: (idx: number) => void, idx: number) => {
    if (language === "es") {
      switch (type) {
        case "finish":
          type = "finalizar"
          break
        case "cancel":
          type = "cancelar"
          break
      }

    }
    confirm({
      title: language === "es" ? `Desea ${type} este Barrido?` : `Do you want to ${type} these Sweep?`,
      icon: <ExclamationCircleFilled />,
      onOk() {
        console.log('OK');
        handler(idx)
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };
  const [sweepStatus] = useState<{ [key: string]: string }>({});
  const iconButtonStyle = { fontSize: 20, cursor: "pointer" };
  const disabledIconButtonStyle = { fontSize: 20, color: "#bfbfbf", cursor: "not-allowed" };
  const finishIconStyle = { fontSize: 20, color: "#52c41a", cursor: "pointer" };
  const stopIconStyle = { fontSize: 20, color: "#ff4d4f", cursor: "pointer" };

  useEffect(() => {
    creatingTimeoutRef.current = {};
  }, [props.sweeps]);

  const handleEnd = async (idx: number) => {
    try {
      var sweepToUpdate = props.sweeps.find(sweep => sweep.idx === idx);
      if (sweepToUpdate === undefined) {
        return;
      }
      sweepToUpdate.status = "processed"
      SweepService.updateStatus(sweepToUpdate).then(() => {
        props.onUpdateContent();
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleStop = async (idx: number) => {
    try {
      var sweepToUpdate = props.sweeps.find(sweep => sweep.idx === idx);
      if (sweepToUpdate === undefined) {
        return;
      }
      sweepToUpdate.status = "stopped"
      SweepService.updateStatus(sweepToUpdate).then(() => {
        props.onUpdateContent();
      });
    } catch (error) {
      console.error(error);
    }
  }
  const handleCSV = (idx: string) => {
    SweepService.statusCSV(idx).then((status) => {
      if (status != null && status === true) {
        SweepService.exportCSV(idx)
      }
      else {
        if (status === false) {
          notification.info({
            message: language === "es" ? "El CSV no está listo" : "CSV is not ready",
            description: language === "es" ? `El CSV para el barrido N°${idx} todavía se está generando` : `CSV for sweep ${idx} is still being generated`,
          });
        }
      }
    })
  }


  const getStatusTag = (status: string, idx: number) => {
    let tagIcon;
    let tagColor;
    let showstatus;
    switch (status) {
      case "processing":
        tagIcon = <SyncOutlined spin />;
        tagColor = "blue";
        showstatus = language === "es" ? "en curso" : "processing"
        break;
      case "processed":
        tagIcon = <CheckCircleOutlined />;
        tagColor = "green";
        showstatus = language === "es" ? "finalizado" : "finished";
        break;
      case "stopped":
        tagIcon = <MinusCircleOutlined />;
        tagColor = "red";
        showstatus = language === "es" ? "cancelado" : "cancelled";
        break;
      case "creating":
        tagIcon = <SyncOutlined spin />;
        tagColor = "yellow";
        showstatus = language === "es" ? "creando" : "creating";
        if (!creatingTimeoutRef.current[idx]) {
          creatingTimeoutRef.current[idx] = true;
          setTimeout(() => {
            props.onUpdateContent();
          }, 10000);
        }
        break;
      default:
        tagIcon = null;
        tagColor = "default";
    }
    return props.isMobile ? <Tag icon={tagIcon} color={tagColor}></Tag> : <Tag icon={tagIcon} color={tagColor}>{showstatus}</Tag>;
  };

  const getDevicesTooltipTitle = (item: SweepBase) => {
    const deviceNames = (item.devices || [])
      .map((device) => device?.name)
      .filter((name): name is string => Boolean(name));

    if (deviceNames.length === 0) {
      return undefined;
    }

    return (
      <div>
        {deviceNames.map((name, index) => (
          <div key={`${item.idx}-${name}-${index}`}>{name}</div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: "",
      key: "idx",
      width: 50,
      responsive: ["lg"] as Breakpoint[],
      render: (item: SweepBase) => item.idx || "",
    },
    {
      title: language === "es" ? "Depósito" : "Warehouse",
      key: "warehouse",
      width: 120,
      render: (item: SweepBase) => {
        const warehouseName = item.warehouse?.name || "";
        const color = getWarehouseReferenceColor(warehouseName);

        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: color,
                display: "inline-block",
              }}
            />
            <span style={{ color, fontWeight: 600 }}>{warehouseName || "-"}</span>
          </span>
        );
      },
    },
    {
      title: language === "es" ? "Número de Conteo" : "Counting number",
      key: "counting_number",
      width: 120,
      render: (item: SweepBase) => item.counting_number || "",
    },
    {
      title: language === "es" ? "Fecha" : "Date",
      key: "timestamp_start",
      width: 120,
      responsive: ["lg"] as Breakpoint[],
      render: (item: SweepBase) => item.timestamp_start.slice(0, 11) || "",
    },
    {
      title: language === "es" ? "Dispositivos" : "Devices",
      key: "devices",
      width: 80,
      align: "center" as const,
      responsive: ["lg"] as Breakpoint[],
      render: (item: SweepBase) => (
        <Tooltip title={getDevicesTooltipTitle(item)}>
          <Tag color="red" style={{ margin: 0, cursor: "help" }}>
            {item.devices.length}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: language === "es" ? "Lecturas" : "Readings count",
      key: "readings_count",
      width: 120,
      align: "center" as const,
      responsive: ["lg"] as Breakpoint[],
      render: (item: SweepBase) => item.readings_count || "0",
    },
    {
      title: language === "es" ? "Estado" : "Status",
      key: "status",
      width: 80,
      render: (item: SweepBase) => getStatusTag(sweepStatus[item.idx] || item.status, item.idx)
    },
    {
      title: language === "es" ? "Acción" : "Action",
      key: "action",
      width: 140,
      align: "center" as const,
      render: (item: SweepBase) => {
        const isDisabled = item.status !== "processing" || !props.writePermission;

        return (
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <Tooltip title={language === "es" ? "Finalizar" : "Finish"}>
              <CheckCircleOutlined
                style={isDisabled ? disabledIconButtonStyle : finishIconStyle}
                onClick={isDisabled ? undefined : () => showConfirm("finish", handleEnd, item.idx)}
              />
            </Tooltip>
            <Tooltip title={language === "es" ? "Cancelar" : "Cancel"}>
              <StopOutlined
                style={isDisabled ? disabledIconButtonStyle : stopIconStyle}
                onClick={isDisabled ? undefined : () => showConfirm("cancel", handleStop, item.idx)}
              />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: language === "es" ? "Detalles" : "Details",
      key: "details",
      width: 120,
      align: "center" as const,
      render: (item: SweepBase) => {
        const isDisabled = item.status === "creating";
        const icon = (
          <EyeTwoTone
            style={isDisabled ? disabledIconButtonStyle : iconButtonStyle}
            twoToneColor={isDisabled ? "#bfbfbf" : "#1677ff"}
          />
        );

        if (isDisabled) {
          return icon;
        }

        return (
          <Link to={`${item.idx}`} aria-label={language === "es" ? "Detalles" : "Details"}>
            {icon}
          </Link>
        );
      },
    },
    {
      title: language === "es" ? "Seguridad" : "Safety",
      key: "safety",
      width: 80,
      align: "center" as const,
      render: (item: SweepBase) => {
        const isDisabled = item.status === "creating" || item.status !== "processing" || !props.securityPermission;
        const icon = <SafetyCertificateOutlined style={isDisabled ? disabledIconButtonStyle : iconButtonStyle} />;

        if (isDisabled) {
          return icon;
        }

        return (
          <Link to={"../security/" + item.idx} aria-label={language === "es" ? "Seguridad" : "Safety"}>
            {icon}
          </Link>
        );
      },
    },
    {
      title: "CSV",
      key: "csv",
      width: 80,
      responsive: ["lg"] as Breakpoint[],
      align: "center" as const,
      render: (item: SweepBase) => {
        const isDisabled = item.status === "creating";

        return (
          <Tooltip title="CSV">
            <FileExcelTwoTone
              style={isDisabled ? disabledIconButtonStyle : iconButtonStyle}
              twoToneColor={isDisabled ? "#bfbfbf" : "#52c41a"}
              onClick={isDisabled ? undefined : () => handleCSV(String(item.idx))}
            />
          </Tooltip>
        );
      },
    },
    {
      title: language === "es" ? "Supervisión" : "Supervision",
      key: "supervision",
      width: 120,
      responsive: ["lg"] as Breakpoint[],
      align: "center" as const,
      render: (item: SweepBase) => {
        const isProcessing = item.status === "processing";
        const isProcessed = item.status === "processed";
        const isDisabled = !isProcessing && !isProcessed;
        const to = isProcessing
          ? `../supervision/${item.warehouse?.idx}`
          : `../supervision/history/${item.warehouse?.idx}/${item.idx}`;
        const icon = <AreaChartOutlined style={isDisabled ? disabledIconButtonStyle : iconButtonStyle} />;

        if (isDisabled) {
          return icon;
        }

        return (
          <Link to={to} aria-label={language === "es" ? "Supervisión" : "Supervision"}>
            {icon}
          </Link>
        );
      },

    },
  ];

  const paginationConfig = {
    current: props.page,
    pageSize: props.pageSize,
    total: props.total,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50"],
    onChange: (page: number, pageSize: number | undefined) => {
      if (pageSize) {
        props.onPageChange(page, pageSize);
      }
    }
  }

  return (
    <Spin
      spinning={props.loading}>

      <Table
        columns={columns}
        dataSource={props.sweeps}
        rowKey="id"
        scroll={{ x: (props.isMobile ? 680 : 1230) }}
        size="small"
        pagination={paginationConfig}
      />
    </Spin>
  );
};

export default SweepsTable;